import { Button, Input, Modal, Select, useToast } from '@/components';
import { useNavigate } from '@solidjs/router';
import type { RunnerMachineOs } from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { useCreateMachine } from './hooks';

/** Shows a freshly issued agent token once, with the config snippet to paste. */
export const TokenReveal = (props: { token: string; apiUrl: string; os: RunnerMachineOs }) => {
  const toast = useToast();
  const config = () => JSON.stringify({ apiUrl: props.apiUrl, token: props.token }, null, 2);
  const path = () =>
    props.os === 'windows'
      ? 'C:\\ProgramData\\hera-agent\\config.json'
      : '~/.hera-agent/config.json';
  const copy = async () => {
    await navigator.clipboard.writeText(config());
    toast.success('Config copied');
  };
  return (
    <div class="space-y-3">
      <p class="text-sm text-amber-700 dark:text-amber-300">
        Copy this now — Hera stores only a hash and will not show the token again.
      </p>
      <div>
        <div class="mb-1 text-xs text-neutral-500">
          Save as <code>{path()}</code>, then follow apps/agent/README.md.
        </div>
        <pre class="overflow-x-auto rounded-lg bg-neutral-100 p-3 text-xs dark:bg-neutral-800">
          {config()}
        </pre>
      </div>
      <Button variant="secondary" size="sm" onClick={copy}>
        Copy config
      </Button>
    </div>
  );
};

export const AddMachineModal = (props: { open: boolean; onClose: () => void; apiUrl: string }) => {
  const create = useCreateMachine();
  const navigate = useNavigate();
  const [name, setName] = createSignal('');
  const [os, setOs] = createSignal<RunnerMachineOs>('macos');
  const [issued, setIssued] = createSignal<{ id: string; token: string } | null>(null);

  const close = () => {
    const done = issued();
    setIssued(null);
    setName('');
    create.reset();
    props.onClose();
    if (done) navigate(`/runners/machines/${done.id}`);
  };

  const submit = async (e: SubmitEvent) => {
    e.preventDefault();
    const res = await create.mutateAsync({ name: name().trim(), os: os() });
    setIssued({ id: res.data.machine.id, token: res.data.token });
  };

  return (
    <Modal open={props.open} onClose={close} title="Add machine" closeOnBackdrop={!issued()}>
      <Show
        when={issued()}
        fallback={
          <form class="space-y-4" onSubmit={submit}>
            <Input
              label="Name"
              required
              value={name()}
              onInput={(e) => setName(e.currentTarget.value)}
              placeholder="UChatMacStudio"
              hint="Letters, digits, dot, dash and underscore."
              error={create.error?.message}
            />
            <Select
              label="OS"
              required
              value={os()}
              onChange={(v) => setOs(v as RunnerMachineOs)}
              options={[
                { value: 'macos', label: 'macOS' },
                { value: 'windows', label: 'Windows' },
              ]}
            />
            <div class="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending} disabled={!name().trim()}>
                Create &amp; issue token
              </Button>
            </div>
          </form>
        }
      >
        {(done) => (
          <div class="space-y-4">
            <TokenReveal token={done().token} apiUrl={props.apiUrl} os={os()} />
            <div class="flex justify-end">
              <Button onClick={close}>Done</Button>
            </div>
          </div>
        )}
      </Show>
    </Modal>
  );
};
